import React, { useContext, useEffect, useState, useCallback } from "react";
import { PrimaryButton } from "components/buttons";
import { submitForm } from "utils/service";
import { businessProduct, personalProduct } from "constants/dropdown";
import TabContext from "context/TabContext";
import parse from "html-react-parser";
import { Alert, Button, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import ReactGA from "react-ga4";

const INIT_FORM_DATA = {
  salutation: "",
  full_name: "",
  // contact_code: "",
  contact_number: "",
  email: "",
  interested_product: "",
  preferred_branch: "",
  ex_customer: "no",
  agree: false,
};

const EnquiryForm = () => {
  const { tabState, enquiryFormRef } = useContext(TabContext);
  const [tab] = tabState;
  const [formRef] = enquiryFormRef;
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [formData, setFormData] = useState(INIT_FORM_DATA);
  const [isRecaptchaVerified, setIsRecaptchaVerified] = useState(false);
  const [isConfirm, setIsConfirm] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");
  const [submitDisabled, setSubmitDisabled] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "contact_number") {
      if (/^\d*$/.test(value)) {
        setFormData({
          ...formData,
          [name]: value,
        });
      }
    } else if (name === "full_name") {
      if (/^[a-zA-Z\s]*$/.test(value)) {
        setFormData({
          ...formData,
          [name]: value,
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: type === "checkbox" ? checked : value,
      });
    }
  };
  const sendFormStartGA = () => {
    ReactGA.event("form_start", {
      action: "Start inserting form details",
      label: window.location.pathname + window.location.search,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationMessage(""); // Reset validation message

    // Validate required fields
    if (!formData.salutation) {
      setValidationMessage("Please select a salutation.");
      return;
    }
    if (!formData.full_name) {
      setValidationMessage("Please enter your full name.");
      return;
    }
    // if (!formData.contact_code) {
    //   setValidationMessage("Please enter your contact code.");
    //   return;
    // }
    if (!formData.contact_number) {
      setValidationMessage("Please enter your contact number.");
      return;
    }
    if (!formData.email) {
      setValidationMessage("Please enter your email.");
      return;
    }
    if (!formData.interested_product) {
      setValidationMessage("Please specify the interested product.");
      return;
    }
    if (!formData.preferred_branch) {
      setValidationMessage("Please select a preferred branch.");
      return;
    }
    if (!formData.ex_customer) {
      setValidationMessage("Please specify if you are an existing customer.");
      return;
    }
    if (!formData.agree) {
      setValidationMessage("Please agree to the terms.");
      return;
    }
    if (!isRecaptchaVerified) {
      setValidationMessage("Please verify that you are not a robot.");
      return;
    }
    setFormData({
      ...formData,
      timestamp: new Date().toISOString(),
    });
    setIsConfirm(true);
  };

  const confirm = async () => {
    ReactGA.event("form_submit", {
      category: "Submit enquiry form",
      action: `Product: ${formData.interested_product}`,
      label: window.location.pathname + window.location.search,
    });

    setSubmitDisabled(true);
    // Handle form submission logic

    try {
      await submitForm(formData);
      setFormData(INIT_FORM_DATA); // Reset form data after successful submission
      setIsSuccess(true);
      setIsError(false);
      setValidationMessage(""); // Clear validation message on success
      setSubmitDisabled(false);
    } catch (error) {
      setErrorMessage(`Error: ${error}`);
      setIsError(true);
      setSubmitDisabled(false);
    }
  };

  const checkConditions = useCallback(() => {
    // const isConsentChecked = formData.agree;
    const submitButton = document.getElementById("submitButton");
    if (submitButton) {
      // Check if conditions to show/hide submit button (if necessary)
      // Not disabling the button as per your requirement
      submitButton.disabled = false; // Keep the button enabled
    }
  }, []);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://www.google.com/recaptcha/api.js";
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    // Define the reCAPTCHA callback function
    window.recaptchaCallback = function () {
      setIsRecaptchaVerified(true);
      checkConditions();
    };

    return () => {
      document.body.removeChild(script);
    };
  }, [checkConditions]);

  useEffect(() => {
    checkConditions(); // Call the function whenever the dependencies change
  }, [checkConditions]);

  return (
    <>
      <Modal show={isSuccess}>
        <Modal.Body>
          <b>Thank you for your interest, we will get back to you shortly.</b>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setIsSuccess(false);
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={errorMessage}>
        <Modal.Body>
          <p>{errorMessage}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setErrorMessage("");
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={isConfirm}>
        <Modal.Body>
          <p>Please review your details before submitting.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setIsConfirm(false);
            }}
          >
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setIsConfirm(false);
              confirm();
            }}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={validationMessage}>
        <Modal.Body>
          <p>{validationMessage}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setValidationMessage("");
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <div
        ref={formRef}
        style={{
          background: "var(--Gray-100, #F2F4F7)",
          borderRadius: "16px",
          padding: "20px",
        }}
      >
        <form className="row g-4 app-form-landing" onSubmit={handleSubmit}>
          <div className="col-xl-4">
            <label className="form-label" htmlFor="salutation">
              <span style={{ color: "#FF5800" }}>*</span>
              <span data-translate="forms_2">Salutation</span>
            </label>
            <select
              id="salutation"
              name="salutation"
              className="form-select"
              required
              value={formData.salutation}
              onChange={(e) => {
                handleChange(e);
                sendFormStartGA();
              }}
            >
              <option value="" disabled>
                --Select--
              </option>
              <option value="mr">Mr.</option>
              <option value="mrs">Mrs.</option>
              <option value="miss">Miss.</option>
            </select>
          </div>

          <div className="col-xl-8">
            <label className="form-label" htmlFor="name">
              <span style={{ color: "#FF5800" }}>*</span>
              <span data-translate="forms_3">Full Name (as per NRIC)</span>
            </label>
            <input
              type="text"
              className="form-control"
              name="full_name"
              required
              value={formData.full_name}
              onChange={handleChange}
            />
          </div>

          <div className="form-group col-12">
            <label className="form-label" htmlFor="code">
              <span style={{ color: "#FF5800" }}>*</span>
              <span data-translate="forms_4">Contact Number</span>
            </label>
            <div className="row">
              {/* <div className="col-xl-4 col-3">
                <input
                  type="text"
                  name="contact_code"
                  className="form-control"
                  placeholder="012"
                  required
                  maxLength="4"
                  pattern="\d{3}"
                  value={formData.contact_code}
                  onChange={handleChange}
                />
              </div> */}
              <div className="col-12">
                <input
                  type="text"
                  name="contact_number"
                  className="form-control"
                  placeholder="0123456789"
                  maxLength="12"
                  required
                  value={formData.contact_number}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className="col-xl-6">
            <label htmlFor="email" className="form-label">
              <span style={{ color: "#FF5800" }}>*</span>
              <span data-translate="forms_5">Email</span>
            </label>
            <input
              type="email"
              name="email"
              className="form-control"
              id="email"
              value={formData.email}
              onChange={handleChange}
            />
          </div>

          <div className="col-xl-6">
            <label className="form-label" htmlFor="income">
              <span style={{ color: "#FF5800" }}>*</span>
              <span data-translate="forms_6">Interested Product</span>
            </label>

            <select
              className="form-select"
              name="interested_product"
              id="interested_product"
              required
              value={formData.interested_product}
              onChange={handleChange}
            >
              {tab !== 1 &&
                personalProduct.map((item) => (
                  <option value={item.value} disabled={item.disabled}>
                    {parse(item.text)}
                  </option>
                ))}
              {tab === 1 &&
                businessProduct.map((item) => (
                  <option value={item.value} disabled={item.disabled}>
                    {item.text}
                  </option>
                ))}
            </select>
          </div>

          <div className="col-xl-12">
            <label className="form-label" htmlFor="branch">
              <span style={{ color: "#FF5800" }}>*</span>
              <span data-translate="forms_7">Your Preferred Branch</span>
            </label>
            <select
              id="preferred_branch"
              name="preferred_branch"
              className="form-select"
              required
              value={formData.preferred_branch}
              onChange={handleChange}
            >
              <option value="" disabled>
                --Select--
              </option>
              <option value="Bangsar">Bangsar Affluent Branch</option>
              <option value="KL Main">KL Main</option>
              <option value="Kota Damansara">Kota Damansara</option>
              <option value="Shah Alam">Shah Alam</option>
              <option value="Ipoh">Ipoh</option>
              <option value="Johor Bahru">Johor Bahru</option>
              <option value="Kota Bharu">Kota Bharu</option>
              <option value="Kota Kinabalu">Kota Kinabalu</option>
              <option value="Kuantan">Kuantan</option>
              <option value="Kuching">Kuching</option>
              <option value="Melaka">Melaka</option>
              <option value="Penang">Penang</option>
              <option value="Sungai Petani">Sungai Petani</option>
            </select>
          </div>

          <div className="col-xl-9">
            <p style={{ textAlign: "left" }} data-translate="forms_8">
              Are you an existing Al Rajhi Banking & Investment Corporation
              Customer?
            </p>
          </div>

          <div className="col-xl-3 justify-content-end">
            <label className="radio-inline me-4" data-translate="forms_9">
              <input
                type="radio"
                name="ex_customer"
                value="yes"
                required
                checked={formData.ex_customer === "yes"}
                onChange={handleChange}
              />
              Yes
            </label>
            <label className="radio-inline" data-translate="forms_10">
              <input
                type="radio"
                name="ex_customer"
                value="no"
                required
                checked={formData.ex_customer === "no"}
                onChange={handleChange}
              />
              No
            </label>
          </div>

          <div className="col-xl-12">
            <p style={{ textAlign: "left" }}>
              (<span style={{ color: "#FF5800" }}>*</span>) Mandatory Fields
            </p>
          </div>
          <div
            className="g-recaptcha"
            data-sitekey="6Ld7zSEUAAAAAPQIdyQzYIfZsn8eAEkODcwGa-iY"
            data-callback="recaptchaCallback"
            style={{ marginTop: 0 }}
          ></div>

          <div className="col-xl-1 col-1">
            <input
              type="checkbox"
              id="consent"
              name="agree"
              value="1"
              required
              checked={formData.agree}
              onChange={handleChange}
            />
          </div>

          <div className="col-xl-11 col-11">
            <p
              style={{ textAlign: "left", fontSize: "14px" }}
              data-translate="forms_12"
            >
              I hereby agree and consent that Al Rajhi Banking & Investment
              Corporation (Malaysia) Bhd may collect, use and process my
              personal information set out in my application, and that I have
              read/understood and agreed to the contents of the{" "}
              <Link to="/about/corporate-governance/compliance">
                Personal Data Protection Notice.
              </Link>
            </p>
          </div>
          <div className="col-4 d-grid gap-2">
            <PrimaryButton
              text="Submit"
              disabled={submitDisabled}
              onClick={handleSubmit}
              id="submitButton"
            />
          </div>
        </form>
      </div>
    </>
  );
};

export default EnquiryForm;
